import Flickity from 'flickity'
import { getSizedImageUrl } from '@shopify/theme-images'
import { productPrice, productPointsValue } from '../helpers/product'

import axios from 'axios'

import {
  defineStore
} from 'pinia'

const SKIO_APP_ID = 'SKIO'

const EU_STORE_DOMAINS = ['sneak-eu.myshopify.com']

const isSubscriptionsMarket = () => {
  const domain = window.theme?.shop?.permanentDomain || ''
  return !EU_STORE_DOMAINS.includes(domain)
}

// Filter selling plan groups based on theme setting (Skio UK/US; none on EU)
const filterSellingPlanGroups = (sellingPlanGroups) => {
  if (!isSubscriptionsMarket()) return []
  if (!sellingPlanGroups || !sellingPlanGroups.length) return sellingPlanGroups

  return sellingPlanGroups.filter(group => group.app_id === SKIO_APP_ID)
}

export const useProductStore = defineStore('ProductStore', {
  state: () => {
    return {
      product: {},
      combinedListingsProductVariants: [],
      combinedListingsProductVariantsMetafields: {},
      lastProduct: {},
      selectedVariant: {},
      selectedSellingPlan: null,
      backInStockPopupIsShowing: false,
    }
  },

  actions: {
    changeVariant (variant) {
      this.selectedVariant = variant
    },

    changeSellingPlan (sellingPlan) {
      this.selectedSellingPlan = sellingPlan
    },

    setInitialVariant () {
      let defaultVariant = this.product.variants[0]
      if (this.product.available) {
        defaultVariant = this.product.variants.find(variant => variant.available)
      }
      this.selectedVariant = defaultVariant
      
      // Update combined metafields after variant is set
      if (this.isCombinedListingsProduct) {
        // Wait for DOM to be ready, with retry logic
        this.updateCombinedMetafieldsWithRetry()
      }
    },

    setInitialProduct () {
      this.product = {
        ...window.theme.product,
        selling_plan_groups: filterSellingPlanGroups(window.theme.product.selling_plan_groups),
        metafields: { ...window.theme.productMetafields },
        inventoryQuantity: window.theme.productInventoryQuantity
      }

      if (window.theme.combinedListingsProductVariants) {
        this.product.variants = window.theme.combinedListingsProductVariants.map(variantProduct => {
          const variantData = { ...variantProduct.variants[0] }
          variantData.featured_image = variantProduct.featured_image
          return variantData
        })

        this.combinedListingsProductVariants = window.theme.combinedListingsProductVariants.map(childProduct => ({
          ...childProduct,
          selling_plan_groups: filterSellingPlanGroups(childProduct.selling_plan_groups)
        }))
        this.combinedListingsProductVariantsMetafields = window.theme.combinedListingsProductVariantsMetafields
      }

      this.lastProduct = this.product

      this.updateJudgeMe()
      // Don't call updateCombinedMetafields here - it will be called in setInitialVariant()
    },

    async updateJudgeMe () {
      const productId = this.isCombinedListingsProduct ? this.combinedListingsProduct.id : this.product.id

      // Update all Judge.me widgets (badges and full review widget)
      const allWidgets = document.querySelectorAll('.jdgm-widget, .jdgm-preview-badge, #judgeme_product_reviews')
      allWidgets.forEach(widget => widget?.setAttribute('data-id', productId))

      setTimeout(() => {
        window.jdgm?.customizeBadges?.()
      }, 300)
    },

    async changeSelectedVariant (variantId) {
      const variant = this.product.variants.find(variant => variant.id === variantId)
      this.selectedVariant = variant
      // update URL
      // const newUrl = `/products/${this.product.handle}?variant=${variantId}`
      // history.replaceState({}, null, newUrl)    

      // update Judge.me
      await this.updateJudgeMe()
      // Update combined metafields with retry logic
      this.updateCombinedMetafieldsWithRetry()
    },

    async changeProductByHandle ({ handle, metafields = null, inventoryQuantity = 99999 }) {
      document.body.classList.add('switching-product')
      
      const pageEndpoint = `${window.Shopify.routes.root}products/${handle}?view=product`
      await axios
        .get(pageEndpoint)
        .then(async response => {
          const pageHTML = response.data
          const temporaryWrapper = document.createElement('div')
          const liveProductSection = document.querySelector('.section-main-product')

          temporaryWrapper.innerHTML = pageHTML
          const productSection = temporaryWrapper.querySelector('.section-main-product')
          const dynamicBlocks = [...productSection.querySelectorAll('[data-block-index]')]

          dynamicBlocks.forEach(block => {
            let blockIndex = block.getAttribute('data-block-index')
            if (block.tagName === 'TEMPLATE') {
              blockIndex = parseInt(blockIndex) - 1
            }

            const liveBlock = liveProductSection.querySelector(`[data-block-index="${blockIndex}"]`)
            if (liveBlock) {
              liveBlock.innerHTML = block.innerHTML
            }
          })
          
        })
        .catch(error => {
          console.log(error)
        })

      const endpoint = `${window.Shopify.routes.root}products/${handle}.js`
      await axios
        .get(endpoint)
        .then(async response => {
          // update last product
          this.lastProduct = this.product

          // update product with filtered selling plan groups
          this.product = {
            ...response.data,
            selling_plan_groups: filterSellingPlanGroups(response.data.selling_plan_groups),
            metafields,
            inventoryQuantity
          }

          // update selectedVariant
          this.setInitialVariant()

          // update URL
          const newUrl = `/products/${response.data.handle}`
          history.replaceState({}, null, newUrl)

          // update Judge.me
          await this.updateJudgeMe()
          // Update combined metafields with retry logic
          this.updateCombinedMetafieldsWithRetry()

          setTimeout(() => { document.body.classList.remove('switching-product') }, 300)
        })
        .catch(error => {
          console.log(error)

          setTimeout(() => { document.body.classList.remove('switching-product') }, 300)
        })
    },

    showBackInStockPopup () {
      this.backInStockPopupIsShowing = true
      document.body.classList.add('overflow-hidden')
    },

    hideBackInStockPopup (event = null) {
      if (!event || event.target.hasAttribute('data-close'))
        this.backInStockPopupIsShowing = false
        document.body.classList.remove('overflow-hidden')
    },

    updateCombinedMetafieldsWithRetry (retries = 10) {
      // Ensure variant is set
      if (!this.selectedVariant || !this.selectedVariant.id) {
        if (retries > 0) {
          setTimeout(() => {
            this.updateCombinedMetafieldsWithRetry(retries - 1)
          }, 100)
          return
        }
      }
      
      const elements = document.querySelectorAll('[data-combined-metafield]')
      
      if (elements.length === 0 && retries > 0) {
        // Elements not ready yet, retry
        setTimeout(() => {
          this.updateCombinedMetafieldsWithRetry(retries - 1)
        }, 100)
        return
      }
      
      // Elements found or max retries reached, proceed with update
      this.updateCombinedMetafields()
    },

    updateCombinedMetafields () {
      if (!this.isCombinedListingsProduct) {
        // Not a combined product, hide all combined metafield elements
        document.querySelectorAll('[data-combined-metafield]').forEach((element) => {
          element.style.display = 'none'
        })
        return
      }

      // Ensure variant is set
      if (!this.selectedVariant || !this.selectedVariant.id) {
        // Variant not set yet, hide elements
        document.querySelectorAll('[data-combined-metafield]').forEach((element) => {
          element.style.display = 'none'
        })
        return
      }

      const combined = this.combinedListingsProduct
      if (!combined || !combined.metafields) {
        // Hide all combined metafield elements if no combined product data
        document.querySelectorAll('[data-combined-metafield]').forEach((element) => {
          element.style.display = 'none'
        })
        return
      }

      const combinedMetafields = {
        'text.short_description': combined.metafields.shortDescription,
        'descriptors.subtitle': combined.metafields.subtitle,
        'custom.offer_information': combined.metafields.offerInformation
      }

      document.querySelectorAll('[data-combined-metafield]').forEach((element) => {
        const key = element.getAttribute('data-combined-metafield')
        const value = combinedMetafields[key]
        
        if (value && value.trim() !== '' && value !== 'null') {
          element.innerHTML = value
          element.style.display = ''
        } else {
          element.innerHTML = ''
          element.style.display = 'none'
        }
      })
    }
  },

  getters: {
    // productType (state) {
    //   let productType = ''
    //   state.product.tags.forEach(tag => {
    //     if (tag.includes('flavour:')) {
    //       productType = 'flavour'
    //     }
    //   })
    //   return productType
    // },

    isCombinedListingsProduct () {
      return this.product.tags?.includes('combined')
    },

    combinedListingsProduct () {
      if (!this.isCombinedListingsProduct) return null

      const product = this.combinedListingsProductVariants.find(product => product.variants.find(variant => variant.id === this.selectedVariant.id))
      const metafieldsProduct = this.combinedListingsProductVariantsMetafields.find(variant => variant.id === this.selectedVariant.id)
      
      return {
        ...product,
        metafields: {...metafieldsProduct?.productMetafields}
      }
    },

    selectedSellingPlanProduct () {
      if (!this.selectedVariant?.id) return null

      if (this.isCombinedListingsProduct) {
        return this.combinedListingsProductVariants.find(product =>
          product.variants?.some(variant => variant.id === this.selectedVariant.id)
        ) || null
      }

      return this.product
    },

    selectedChildHasSellingPlan () {
      const product = this.selectedSellingPlanProduct
      if (!product) return false

      const groups = filterSellingPlanGroups(product.selling_plan_groups)
      return groups.length > 0
    },

    subscriptionPerksVisible () {
      return this.selectedChildHasSellingPlan && this.selectedSellingPlan !== null
    },

    productHasSubscriptionOption () {
      return this.product.selling_plan_groups && this.product.selling_plan_groups.length > 0
    },

    productHasPurchaseLimit () {
      return !!this.product.tags.find(tag => tag.includes('limit-'))
    },

    purchaseLimit () {
      return this.productHasPurchaseLimit ? parseInt(this.product.tags.find(tag => tag.includes('limit-')).replace('limit-', '')) : 999999
    },

    flavourCollectionHandle (state) {
      const servingTypes = window.theme.productTags.servingTypeTagKeys.split(',')
      let isValidServingType = false

      servingTypes.forEach(servingType => {
        const tag = window.theme.productTags[`${servingType}Tag`]
        if (state.product.tags.includes(tag)) {
          isValidServingType = true
        }
      })

      if (isValidServingType) {
        const flavourTag = state.product.tags.find(tag => tag.includes('flavour:'))
        if (flavourTag) {
          const flavourCollectionHandle = flavourTag.replace('flavour:', '')
          return flavourCollectionHandle
        }
      }

      return null
    },

    defaultVariant (state) {
      let defaultVariant = state.product.variants[0]
      if (state.product.available) {
        defaultVariant = state.product.variants.find(variant => variant.available)
      }
      return defaultVariant
    },

    shortDescription (state) {
      return state.product.metafields.shortDescription
    },

    priceFormatted (state) {
      if (state.selectedSellingPlan) {
        return state.selectedSellingPlan.price
      }
      return productPrice(state.product.price)
    },
    
    variantPriceFormatted (state) {
      if (state.selectedSellingPlan) {
        return state.selectedSellingPlan.price
      }

      return productPrice(state.selectedVariant.price)
    },

    saleBadge (state) {
      let saleBadge = null

      state.product.tags.forEach(tag => {
        if (tag.includes('promo-badge:')) {
          saleBadge = tag.split('promo-badge:')[1]
          return
        }
      })
      return saleBadge
    },

    badges (state) {
      let badges = []

      state.product.tags.forEach(tag => {
        if (tag.includes('badge-')) badges.push(tag.split('badge-')[1])
      })
      return badges.length > 0 ? badges : null
    },

    gradientBackgroundColours (state) {
      return state.product.metafields ? state.product.metafields.gradientBackgroundColours : null
    },

    hasColouredBgOption (state) {
      // let hasColouredBgOption = false
      // if (state.product.images[0].includes('.jpg')) {
      //   const colouredBgProductTagKeys = window.theme.productTags.colouredBgProductTagKeys.split(',')
      //   colouredBgProductTagKeys.forEach(tagKey => {
      //     const tag = window.theme.productTags[`${tagKey}Tag`]
      //     if (state.product.tags.includes(tag)) {
      //       hasColouredBgOption = true
      //       return
      //     }
      //   })
      // }
      //
      // return hasColouredBgOption

      return state.product.images[0].includes('.jpg') && state.product.images[1].includes('.png')
    },

    variantCollectionTriggerImage () {
      return getSizedImageUrl(this.galleryMedia[0].preview_image.src, '48x')
    },

    variantTriggerImage () {
      return getSizedImageUrl(this.selectedVariant.featured_image, '48x')
    },

    galleryMedia (state) {
      let media = state.product.media.filter((image, index) => {
        // First filter out the coloured background option if needed
        if (this.hasColouredBgOption && index === 0) {
          return false
        }

        // Then handle combined listings product filtering
        if (this.isCombinedListingsProduct) {
          const formattedImageSrc = image.preview_image.src.split('?')[0].split('files/')[1]
          const isVariantImage = this.combinedListingsProductVariants.some(variant => {
            const variantMedia = variant.media.find(media => media.media_type === 'image')
            if (!variantMedia) return false
            const formattedVariantImageSrc = variantMedia.preview_image.src.split('?')[0].split('files/')[1]
            return formattedImageSrc === formattedVariantImageSrc
          })
          return !isVariantImage
        }

        return true
      })

      return media
    },

    thumbnailImages() {
      return this.galleryMedia.map((m) =>
        getSizedImageUrl(m.preview_image.src, '246x')
      )
    },

    largeMedia () {
      return this.galleryMedia.map((image) => ({
        ...image,
        preview_image: {
          ...image.preview_image,
          src: getSizedImageUrl(image.preview_image.src, '1400x'),
          mobileSrc: getSizedImageUrl(image.preview_image.src, '600x'),
        },
      }))
    },
  },
})
